import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatBRL, formatDateBR } from "../lib/api";
import { Layers, Plus, FileText, Handshake, ArrowRight, Loader2 } from "lucide-react";
import NewInstallmentContractModal from "./NewInstallmentContractModal";

export default function InstallmentContractsSection({ clientId, canManage }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/installment-contracts`);
      setItems(Array.isArray(data) ? data : []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (clientId) load(); }, [clientId]);

  return (
    <section className="fc-card p-6 sm:p-7" data-testid="installment-contracts-section">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
            <Layers className="text-[#D4AF37]" size={20} />
          </div>
          <div>
            <h2 className="font-display text-[17px] font-semibold tracking-tight">Parcelados e Acordos</h2>
            <p className="text-[12.5px] text-[#A3A3A3] mt-0.5">Operações alternativas — não se misturam com contratos normais de 30 dias.</p>
          </div>
        </div>
        {canManage && (
          <button onClick={() => setOpenNew(true)} className="fc-btn-gold flex items-center gap-2" data-testid="new-ic-from-client">
            <Plus size={14} /> Novo
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#737373] text-sm py-4">
          <Loader2 size={14} className="animate-spin" /> Carregando...
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-[#262626] bg-[#0a0a0a] p-6 text-center text-[12.5px] text-[#737373]">
          Nenhum parcelado ou acordo para este cliente.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((ic) => {
            const Icon = ic.kind === "parcelado" ? FileText : Handshake;
            const cls = ic.kind === "parcelado" ? "text-blue-300 border-blue-500/30 bg-blue-500/10" : "text-violet-300 border-violet-500/30 bg-violet-500/10";
            return (
              <Link
                key={ic.id}
                to={`/parcelados/${ic.id}`}
                className="block p-3 rounded-md border border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#D4AF37]/40 transition-colors"
                data-testid={`client-ic-${ic.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`badge-base border ${cls} text-[10px] flex-shrink-0`}>
                      <Icon size={9} /> {ic.kind.toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] text-white font-medium">
                        {formatBRL(ic.total_amount)} em {ic.installments_count}x
                        <span className="text-[#737373] ml-1.5">({ic.frequency === "weekly" ? "sem" : "mês"})</span>
                      </div>
                      <div className="text-[11px] text-[#737373] mt-0.5">
                        {ic.paid_count}/{ic.installments_count} pagas · Saldo: {formatBRL(ic.balance_remaining)}
                        {ic.next_due && ic.status === "ativo" && <> · Próx: {formatDateBR(ic.next_due.due_date)}</>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`badge-base text-[10px] border ${
                      ic.status === "quitado" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" :
                      ic.status === "cancelado" ? "bg-zinc-500/15 text-zinc-300 border-zinc-500/40" :
                      (ic.overdue_count > 0) ? "bg-red-500/15 text-red-300 border-red-500/40" :
                      "bg-amber-500/15 text-amber-300 border-amber-500/40"
                    }`}>
                      {ic.status === "quitado" ? "Quitado" : ic.status === "cancelado" ? "Cancelado" : (ic.overdue_count > 0) ? "Atrasado" : "Em dia"}
                    </span>
                    <ArrowRight size={12} className="text-[#737373]" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {openNew && (
        <NewInstallmentContractModal
          prefilledClientId={clientId}
          onClose={() => setOpenNew(false)}
          onCreated={() => { setOpenNew(false); load(); }}
        />
      )}
    </section>
  );
}
